import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import './Services.css';
import LanguageSelectorDropdown from '../../components/LanguageSelectorDropdown';
import apiService from '../../services/api';
import { useOrder } from '../../context/OrderContext';

const Services = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setQuoteDetails, setCurrentStep } = useOrder();
  const fileInputRef = useRef(null);
  const [translationType, setTranslationType] = useState('');
  const [translationTypes, setTranslationTypes] = useState([]);
  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const [quoteData, setQuoteData] = useState(null);

  const [language, setLanguage] = useState({ 
    from: { code: '', id: '' }, 
    to: { code: '', id: '' } 
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const [validationErrors, setValidationErrors] = useState({
    file: false,
    languages: false
  });
  
  // Fetch translation types on component mount
  useEffect(() => {
    apiService.translations.getTypes()
      .then(response => {
        const types = response.data;
        setTranslationTypes(types);
        if (types.length > 0) {
          setTranslationType(types[0].id.toString());
        }
      })
      .catch(error => {
        console.error('Error fetching translation types:', error);
      });
  }, []);
  
  // Handle language change
  const handleLanguageChange = (type) => (e) => {
    if (e.target.selectedLanguage) {
      setLanguage({
        ...language,
        [type]: {
          code: e.target.value,
          id: e.target.selectedLanguage.id
        }
      });
    } else {
      setLanguage({
        ...language,
        [type]: {
          code: e.target.value,
          id: ''
        }
      });
    }
    
    // Clear language validation error when user selects language
    if (validationErrors.languages) {
      setValidationErrors(prev => ({ ...prev, languages: false }));
      setError('');
    }
  };
  
  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    validateAndSetFile(file);
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    validateAndSetFile(file);
  };

  // Validate file (type, size, etc.)
  const validateAndSetFile = (file) => {
    setError('');
    
    if (!file) return;
    
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'];
    const maxFileSize = 20 * 1024 * 1024; // 20MB
    
    if (!validTypes.includes(file.type)) {
      setError(t('services.invalid-file-type'));
      return;
    }
    
    if (file.size > maxFileSize) {
      setError(t('services.file-size-error'));
      return;
    }
    
    setSelectedFile(file);
    
    // Clear file validation error when user selects file
    if (validationErrors.file) {
      setValidationErrors(prev => ({ ...prev, file: false }));
      setError('');
    }
  };
  
  // Remove the selected file
  const removeFile = () => {
    setSelectedFile(null);
  };
  const handleWhatsAppRedirect = () => {
    // WhatsApp number should be configured properly
    const whatsappNumber = "+96896758894"; // Replace with actual WhatsApp number
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(t('translation_order.whatsapp_message'))}`;
    window.open(whatsappUrl, '_blank');
  };


  // Trigger file input click
  const browseFiles = () => {
    fileInputRef.current.click();
  };

  // Handle close popup function
  const handleClosePopup = () => {
    setShowReviewPopup(false);
  };

  // Handle calculate price with proper Arabic translation
  const handleCalculatePrice = () => {
    // Reset previous errors
    setValidationErrors({ file: false, languages: false });
    setError('');
    
    // Check for validation errors
    let hasErrors = false;
    const errors = { file: false, languages: false };
    
    if (!selectedFile) {
      errors.file = true;
      hasErrors = true;
    }
    
    if (!language.from.id || !language.to.id) {
      errors.languages = true;
      hasErrors = true;
    }
    
    if (hasErrors) {
      setValidationErrors(errors);
      
      // Show specific error messages using translation keys
      if (errors.file && errors.languages) {
        setError(t('services.file-and-language-required'));
      } else if (errors.file) {
        setError(t('services.file-required'));
      } else if (errors.languages) {
        setError(t('services.language-required'));
      }
      
      // Scroll to error message
      setTimeout(() => {
        document.querySelector('.error-message')?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }, 100);
      
      return;
    }
    
    // If validation passes, proceed with API call
    setIsLoading(true);
    
    const formData = new FormData();
    formData.append('document', selectedFile);
    formData.append('source_language', language.from.id);
    formData.append('target_language', language.to.id);
    formData.append('translation_type', translationType);
    
    apiService.translations.getQuote(formData)
      .then(response => {
        console.log('Quote response:', response.data);
        const quoteData = response.data;
        
        setIsLoading(false);
        
        setQuoteDetails(quoteData);
        setCurrentStep(1);
        setQuoteData(quoteData);
        if (quoteData.needs_manual_review) {
          setShowReviewPopup(true);
          return;
        }
        navigate('/translation-summary', { 
          state: { 
            quoteDetails: quoteData,
            file: selectedFile,
            sourceLanguage: language.from.id,
            targetLanguage: language.to.id,
            translationType: translationType
          } 
        });
      })
      .catch(error => {
        setIsLoading(false);
        console.error('Error getting quote:', error);
        
        if (error.code === 'ECONNABORTED') {
          setError(t('services.upload-timeout'));
        } else if (error.response) {
          if (error.response.status === 413) {
            setError(t('services.file-too-large'));
          } else if (error.response.data && error.response.data.message) {
            setError(error.response.data.message);
          } else if (error.response.data && typeof error.response.data === 'object') {
            const errorMessages = [];
            Object.keys(error.response.data).forEach(key => {
              if (Array.isArray(error.response.data[key])) {
                errorMessages.push(`${key}: ${error.response.data[key].join(', ')}`);
              }
            });
            
            if (errorMessages.length > 0) {
              setError(errorMessages.join(' | '));
            } else {
              setError(t('services.quote-error'));
            }
          } else {
            setError(t('services.quote-error'));
          }
        } else if (error.request) {
          setError(t('services.network-error'));
        } else {
          setError(t('services.quote-error'));
        }
      });
  };

  return (
    <div className="services-page">
      <div className="container">
      <div className="services-header">
      <h1 className="services-title">{t('services.page-title')}</h1>
      <p className="services-subtitle">{t('services.page-subtitle')}</p>
      </div>
        <div className="services-container" style={{ position: 'relative' }}>
          {error && <div className="error-message">{error}</div>}
          
          <div className="services-form">
            <div className="services-form-right">
              <div className="form-section">
                <h3 className="form-section-title">{t('services.translation-type')}</h3>
                <div className="translation-type-options">
                  {translationTypes.length > 0 ? (
                    translationTypes.map(type => (
                      <div className="option" key={type.id}>
                        <input 
                          type="radio" 
                          id={`type-${type.id}`} 
                          name="translationType" 
                          checked={translationType === type.id.toString()}
                          onChange={() => setTranslationType(type.id.toString())}
                        />
                        <label 
                          htmlFor={`type-${type.id}`} 
                          className={translationType === type.id.toString() ? 'selected' : ''}
                          title={type.description}
                        >
                          {type.name}
                          {type.is_premium && <span className="premium-badge">Premium</span>}
                        </label>
                      </div>
                    ))
                  ) : (
                    <div>Loading translation types...</div>
                  )}
                  
                  {translationTypes.find(type => 
                    type.id.toString() === translationType && 
                    (type.name === "أخرى" || type.name === "Other")
                  ) && (
                    <div className="other-type-note">
                      {t('services.other-type-note')}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="form-section language-section">
                <h3 className="form-section-title">{t('services.language')}</h3>
                <div className={`language-inputs ${validationErrors.languages ? 'error' : ''}`}>
                  <LanguageSelectorDropdown 
                    id="from" 
                    value={language.from.code}
                    onChange={handleLanguageChange('from')}
                    label={t('services.from')}
                    placeholder={t('services.selectLanguage')}
                    isSource={true}
                  />
                  <LanguageSelectorDropdown 
                    id="to" 
                    value={language.to.code}
                    onChange={handleLanguageChange('to')}
                    label={t('services.to')}
                    placeholder={t('services.selectLanguage')}
                    isSource={false}
                  />
                </div>
              </div>
            </div>
            <div className="services-form-left">
              <div className="file-upload-section">
                <div 
                  className={`upload-box ${isDragging ? 'dragging' : ''} ${validationErrors.file ? 'error' : ''}`}
                  onClick={browseFiles}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    accept=".pdf,.doc,.docx,.ppt,.pptx"
                  />
                  
                  {selectedFile ? (
                    <div className="selected-file">
                      <div className="file-info">
                        <span className="file-name">{selectedFile.name}</span>
                        <span className="file-size">({(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)</span>
                      </div>
                      <button 
                        type="button" 
                        className="remove-file-btn" 
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile();
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <>
                    <div className="upload-icon-container">
                      <svg width="120" height="120" viewBox="0 0 80 70" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M56.25 26.875C66.25 27.5 73.75 35.625 73.75 45C73.75 55 65.625 63.125 55.625 63.125H21.25C13.125 63.125 6.25 56.25 6.25 48.125C6.25 40.625 11.875 34.375 19.375 33.125C21.25 23.125 30 15.625 40 15.625C47.5 15.625 54.375 20 56.25 26.875Z" stroke="#333333" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M40 45L40 30" stroke="#333333" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M31.875 38.125L40 30L48.125 38.125" stroke="#333333" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <p className="upload-text">{t('services.upload-text')}</p>
                    <p className="upload-details">{t('services.upload-details')}</p>
                    <p className="upload-limit">{t('services.upload-limit')}</p>
                    </>
                  )}
                </div>
              </div>
            </div>
            <button 
              className="calculate-price-btn" 
              onClick={handleCalculatePrice}
              disabled={isLoading}
            >
              {isLoading ? t('services.loading') : t('services.calculate-price')}
            </button>
          </div>
          
          {isLoading && (
            <div className="loader-overlay">
              <div className="loader-container">
                <svg width="143" height="143" viewBox="0 0 143 143" fill="none" xmlns="http://www.w3.org/2000/svg" className="loader-svg">
                  <circle cx="71.4808" cy="71.4806" r="44.5156" transform="rotate(150 71.4808 71.4806)" stroke="#17224D" strokeWidth="1.58984"/>
                  <path d="M32.9127 93.7479C29.4999 95.7182 28.2943 100.12 30.7157 103.229C34.8743 108.569 40.0563 113.058 45.9829 116.421C53.9862 120.962 63.0569 123.283 72.2575 123.145C81.4582 123.006 90.455 120.413 98.3181 115.634C104.141 112.095 109.186 107.452 113.182 101.99C115.509 98.8091 114.171 94.4452 110.701 92.5784C107.23 90.7115 102.951 92.069 100.46 95.1225C97.782 98.4045 94.5551 101.221 90.906 103.439C85.2146 106.899 78.7025 108.776 72.043 108.876C65.3834 108.976 58.818 107.296 53.0251 104.009C49.311 101.902 46.0009 99.183 43.2258 95.9829C40.644 93.0058 36.3254 91.7775 32.9127 93.7479Z" fill="#17224D"/>
                </svg>
                <p className="loader-text">{t('services.calculating-price')}</p>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Manual Review Popup */}
      {showReviewPopup && (
        <div className="review-popup-overlay">
          <div className="review-popup">
            <div className="review-popup-content">
              <h3>{t('translation_order.manual_review_title')}</h3>
              <p>{quoteData.reviewMessage || t('translation_order.manual_review_default_message')}</p>
              <div className="review-popup-actions">
                <button className="whatsapp-btn" onClick={handleWhatsAppRedirect}>
                  {t('translation_order.contact_via_whatsapp')}
                </button>
                <button className="close-btn" onClick={handleClosePopup}>
                  {t('translation_order.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Services;